/**
 * Mutation hooks for note CRUD operations
 *
 * Provides React Query mutations wrapping the notes API endpoints.
 * Each mutation invalidates relevant query keys on success.
 */

import {
  createNote,
  updateNote,
  deleteNote,
  uploadNotePhoto,
  deleteNotePhoto,
} from '@/api/endpoints/notes';
import { NOTES_QUERY_KEYS } from '@/api/queries/notes';
import { NoteCreate, NoteUpdate, NoteWire } from '@/api/schemas/note';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Hook for creating a new note
 *
 * On success, invalidates all note query keys so lists refresh.
 */
export function useCreateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: NoteCreate) => createNote(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook for updating an existing note's content
 *
 * On success, invalidates all note query keys so lists and detail views refresh.
 */
export function useUpdateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: NoteUpdate }) =>
      updateNote(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEYS.all });
      queryClient.invalidateQueries({
        queryKey: NOTES_QUERY_KEYS.detail(variables.id),
      });
    },
  });
}

/**
 * Hook for deleting a note
 *
 * On success, invalidates all note query keys so lists refresh.
 */
export function useDeleteNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteNote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook for uploading a photo to a note (max 1 per note)
 *
 * On success, invalidates the specific note detail and all note lists.
 */
export function useUploadNotePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      noteId,
      file,
    }: {
      noteId: string;
      file: { uri: string; type?: string; name?: string; file?: File };
    }) => uploadNotePhoto(noteId, file),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEYS.all });
      queryClient.invalidateQueries({
        queryKey: NOTES_QUERY_KEYS.detail(variables.noteId),
      });
    },
  });
}

/**
 * Hook for deleting a note's photo
 *
 * On success, invalidates the specific note detail and all note lists.
 */
export function useDeleteNotePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteId: string) => deleteNotePhoto(noteId),
    onSuccess: (_result, noteId) => {
      queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEYS.all });
      queryClient.invalidateQueries({
        queryKey: NOTES_QUERY_KEYS.detail(noteId),
      });
    },
  });
}
